import type { ReactNode } from "react";
import { AlertTriangle, Loader2, RotateCw } from "lucide-react";
import { errorMessage } from "../../api.js";
import { cx } from "../../lib/cx.js";

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <Loader2
      className={cx("animate-spin shrink-0", className ?? "size-4")}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? "img" : undefined}
    />
  );
}

/** Bloque gris animado para reservar espacio mientras carga (evita saltos de layout). */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cx("animate-pulse rounded-md bg-surface-3", className)} />;
}

export function LoadingBlock({ label = "Cargando…", className }: { label?: string; className?: string }) {
  return (
    <div role="status" className={cx("flex items-center justify-center gap-2 py-10 text-muted", className)}>
      <Spinner />
      <span>{label}</span>
    </div>
  );
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cx("flex flex-col items-center justify-center gap-2 px-4 py-10 text-center", className)}>
      {icon && <div className="mb-1 text-subtle [&>svg]:size-8">{icon}</div>}
      <p className="text-[15px] font-semibold text-fg">{title}</p>
      {description && <div className="max-w-md text-muted">{description}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

interface ErrorStateProps {
  error: unknown;
  title?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({ error, title = "No se pudo cargar la información", onRetry, className }: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cx(
        "flex flex-col items-center justify-center gap-2 rounded-lg border border-danger/30 bg-danger-soft px-4 py-8 text-center",
        className,
      )}
    >
      <AlertTriangle className="size-6 text-danger" aria-hidden="true" />
      <p className="font-semibold text-danger-soft-fg">{title}</p>
      <p className="max-w-md text-danger-soft-fg/90">{errorMessage(error)}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-danger/40 px-3 py-1.5 font-medium text-danger-soft-fg hover:bg-danger/10"
        >
          <RotateCw className="size-4" aria-hidden="true" />
          Reintentar
        </button>
      )}
    </div>
  );
}

/** Mensaje en línea (alerta, información) con ícono: el color nunca va solo. */
export function Callout({
  tone = "info",
  icon,
  title,
  children,
  className,
}: {
  tone?: "info" | "warning" | "danger" | "success";
  icon?: ReactNode;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const tones = {
    info: "border-accent/25 bg-accent-soft text-accent-soft-fg",
    warning: "border-warning/30 bg-warning-soft text-warning-soft-fg",
    danger: "border-danger/30 bg-danger-soft text-danger-soft-fg",
    success: "border-success/30 bg-success-soft text-success-soft-fg",
  } as const;
  return (
    <div
      role={tone === "danger" ? "alert" : undefined}
      className={cx("flex gap-2.5 rounded-lg border px-3 py-2.5", tones[tone], className)}
    >
      {icon && <span className="mt-0.5 shrink-0 [&>svg]:size-4" aria-hidden="true">{icon}</span>}
      <div className="min-w-0 space-y-0.5">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="text-[13px] leading-snug">{children}</div>}
      </div>
    </div>
  );
}
